import {
  Component,
  DestroyRef,
  ElementRef,
  inject,
  Input,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, of } from 'rxjs';
import { catchError, debounceTime, map, switchMap } from 'rxjs/operators';
import {
  AlternativeStepperComponent,
  IStep,
} from '../../../../../components/ui/alternative-stepper/alternative-stepper.component';
import { PromptCardComponent } from '../../../../../components/toasts-and-cards/prompt-card/prompt-card.component';
import {
  COUNTRIES,
  CountryInfo,
  CreateUssdApplicationRequest,
  UssdApplication,
} from '../../../../../types/ussd.models';
import { UssdApplicationsService } from '../../../../../services/ussd/ussd-applications.service';
import { UserProfileService } from '../../../../../services/user/user-profile.service';
import { SettingsService } from '../../../../../services/settings/settings.service';
import { ToastService } from '../../../../../services/toast/toast.service';
import { IUserOrganization } from '../../../../../types/settingsDataModule';
import {
  aliasFromName,
  countryByCode,
  describeHttpError,
  environmentLabel,
  normaliseAuthType,
  USSD_AUTH_TYPES,
  UssdAuthType,
} from '../../ussd-display';

export type ApplicationFormMode = 'create' | 'edit';
type FormStep = 'basics' | 'callback' | 'review';

const STEP_ORDER: FormStep[] = ['basics', 'callback', 'review'];

type CredentialFields = Pick<
  CreateUssdApplicationRequest,
  | 'callbackBearerToken'
  | 'callbackBasicUsername'
  | 'callbackBasicPassword'
  | 'callbackApiKey'
  | 'callbackApiKeyHeader'
  | 'callbackCustomAuthHeader'
  | 'callbackCustomAuthValue'
>;

/** Callbacks always go out as a JSON POST; the gateway offers no other choice. */
const CALLBACK_METHOD = 'POST';
const CALLBACK_CONTENT_TYPE = 'application/json';

/**
 * The create and edit wizards for a USSD application: three steps driven by
 * the `?title=` query param (basics, callback, review), shared by
 * CreateApplicationComponent and EditApplicationComponent.
 */
@Component({
  selector: 'bk-ussd-application-form',
  standalone: true,
  imports: [FormsModule, AlternativeStepperComponent, PromptCardComponent],
  templateUrl: './application-form.component.html',
  styleUrl: './application-form.component.css',
})
export class UssdApplicationFormComponent implements OnInit {
  @Input() mode: ApplicationFormMode = 'create';
  /** Application id, edit mode only. */
  @Input() id = '';

  @ViewChild('countryInput') private countryInput?: ElementRef<HTMLInputElement>;

  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  private applicationsService = inject(UssdApplicationsService);
  private userService = inject(UserProfileService);
  private settingsService = inject(SettingsService);
  private toast = inject(ToastService);
  private host: ElementRef<HTMLElement> = inject(ElementRef);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  readonly steps: IStep[] = [
    { stepName: 'Basics', stepQuery: 'basics' },
    { stepName: 'Callback', stepQuery: 'callback' },
    { stepName: 'Review', stepQuery: 'review' },
  ];
  readonly AUTH_TYPES = USSD_AUTH_TYPES;
  readonly COUNTRIES = COUNTRIES;
  readonly method = CALLBACK_METHOD;
  readonly contentType = CALLBACK_CONTENT_TYPE;
  readonly environmentLabel = environmentLabel;

  activeStep: FormStep = 'basics';

  // Loading the app (edit)
  isLoading = false;
  loadError = '';
  original: UssdApplication | null = null;

  // Submitting
  isSubmitting = false;
  submitError = '';

  // Step 1: basics
  name = '';
  alias = '';
  /** Once the alias is typed by hand it stops following the name. */
  aliasTouched = false;
  description = '';
  serviceName = '';
  selectedCountry: CountryInfo | null = null;
  serviceCode = '';
  codeState: 'idle' | 'loading' | 'error' = 'idle';
  codeError = '';
  /** Country the current code was generated for, so a re-pick does not allocate again. */
  private codeCountry = '';
  /** Counts code requests so only the latest pick's answer is applied. */
  private codeRequest = 0;
  /** Edit mode: country and code stay locked until the change is confirmed. */
  codeUnlocked = false;
  showChangeCodeConfirm = false;

  // Country combobox
  countrySearch = '';
  showCountryList = false;
  activeCountryIndex = -1;
  readonly countryListId = `ussd-country-list-${Math.random().toString(36).slice(2, 8)}`;

  // Step 2: callback
  callbackUrl = '';
  authType: UssdAuthType = 'NONE';
  /** Stored auth type the gateway does not support (OAUTH2 from older versions). */
  legacyAuthType: string | null = null;
  bearerToken = '';
  basicUsername = '';
  basicPassword = '';
  apiKeyHeader = '';
  apiKey = '';
  customAuthHeader = '';
  customAuthValue = '';
  revealSecret = false;

  serviceAccountId = '';

  // Validation
  nameError = '';
  countryError = '';
  serviceCodeError = '';
  callbackUrlError = '';
  credentialErrors: Record<string, string> = {};

  // Duplicate checks
  nameTaken = false;
  codeTaken = false;
  private nameCheck$ = new Subject<string>();

  get isEdit(): boolean {
    return this.mode === 'edit';
  }

  get organisationId(): string {
    return this.userService.organizationId ?? '';
  }

  get title(): string {
    if (!this.isEdit) return 'New application';
    return this.original?.name ? `Edit ${this.original.name}` : 'Edit application';
  }

  get subtitle(): string {
    return this.isEdit
      ? 'Changes apply to new sessions straight away. Sessions in progress finish on the old settings.'
      : 'Name the application, pick its country for a service code, then tell us where to send each session.';
  }

  get codeLocked(): boolean {
    return this.isEdit && !this.codeUnlocked;
  }

  get filteredCountries(): CountryInfo[] {
    const q = this.countrySearch.toLowerCase().trim();
    if (!q) return this.COUNTRIES;
    return this.COUNTRIES.filter(
      (c) => c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q) || c.dialCode.includes(q)
    );
  }

  get authHint(): string {
    return this.AUTH_TYPES.find((a) => a.value === this.authType)?.hint ?? '';
  }

  get authLabel(): string {
    return this.AUTH_TYPES.find((a) => a.value === this.authType)?.label ?? this.authType;
  }

  get callbackIsInsecure(): boolean {
    return /^http:\/\//i.test(this.callbackUrl.trim());
  }

  get submitLabel(): string {
    if (this.isSubmitting) return this.isEdit ? 'Saving…' : 'Creating…';
    return this.isEdit ? 'Save changes' : 'Create application';
  }

  // ── Lifecycle ──

  ngOnInit(): void {
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const title = params['title'] as FormStep;
      this.activeStep = title && STEP_ORDER.includes(title) ? title : 'basics';
    });

    // No distinctUntilChanged: onNameChange clears nameTaken on every
    // keystroke, so a name typed back to an earlier value must be re-checked.
    this.nameCheck$
      .pipe(
        debounceTime(400),
        switchMap((name) =>
          name && this.organisationId
            ? this.applicationsService
                .checkDuplicate(this.organisationId, {
                  name,
                  serviceCode: this.serviceCode,
                  excludeId: this.isEdit ? this.id : undefined,
                })
                .pipe(
                  map((result) => ({ name, taken: !!result?.nameExists })),
                  catchError(() => of(null))
                )
            : of(null)
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((result) => {
        // Ignore an answer for a name that has since been edited.
        if (result && result.name === this.name.trim()) this.nameTaken = result.taken;
      });

    if (this.isEdit) {
      this.aliasTouched = true;
      this.loadApplication();
    } else {
      this.settingsService.getUserInstitutionData
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((data: IUserOrganization | null) => {
          if (data?.attributes?.serviceAccountId) {
            this.serviceAccountId = data.attributes.serviceAccountId;
          }
        });
      this.settingsService.fetchUserInstitution();
    }
  }

  loadApplication(): void {
    if (!this.organisationId || !this.id) return;

    this.isLoading = true;
    this.loadError = '';
    this.applicationsService
      .getApplicationById(this.organisationId, this.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (app) => {
          this.original = app;
          this.name = app.name ?? '';
          this.alias = app.alias ?? '';
          this.serviceCode = app.serviceCode ?? '';
          this.description = app.description ?? '';
          this.serviceName = app.callbackServiceName ?? '';
          this.callbackUrl = app.callbackUrl1 ?? '';
          const auth = normaliseAuthType(app.callbackAuthType);
          this.authType = auth.type;
          this.legacyAuthType = auth.legacy;
          this.bearerToken = app.callbackBearerToken ?? '';
          this.basicUsername = app.callbackBasicUsername ?? '';
          this.basicPassword = app.callbackBasicPassword ?? '';
          this.apiKeyHeader = app.callbackApiKeyHeader ?? '';
          this.apiKey = app.callbackApiKey ?? '';
          this.customAuthHeader = app.callbackCustomAuthHeader ?? '';
          this.customAuthValue = app.callbackCustomAuthValue ?? '';
          this.selectedCountry =
            countryByCode(app.countryCode) ??
            (app.countryCode
              ? { code: app.countryCode, name: app.countryName || app.countryCode, flag: '', dialCode: '' }
              : null);
          this.codeCountry = this.selectedCountry?.code ?? '';
          this.isLoading = false;
        },
        error: (err) => {
          this.loadError = describeHttpError(err, 'The application could not be loaded.');
          this.isLoading = false;
        },
      });
  }

  // ── Basics ──

  onNameChange(): void {
    this.nameError = '';
    this.nameTaken = false;
    if (!this.aliasTouched) this.alias = aliasFromName(this.name);
    this.nameCheck$.next(this.name.trim());
  }

  onAliasChange(): void {
    this.aliasTouched = true;
    this.alias = this.alias.toUpperCase().replace(/[^A-Z0-9_]/g, '_');
  }

  // Country combobox

  openCountryList(): void {
    if (this.codeLocked) return;
    this.showCountryList = true;
    this.activeCountryIndex = -1;
  }

  closeCountryList(): void {
    this.showCountryList = false;
    this.activeCountryIndex = -1;
  }

  onCountrySearch(): void {
    this.showCountryList = true;
    this.activeCountryIndex = this.filteredCountries.length ? 0 : -1;
  }

  onCountryKeydown(event: Event): void {
    const e = event as KeyboardEvent;
    const list = this.filteredCountries;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this.showCountryList = true;
        this.activeCountryIndex = list.length ? (this.activeCountryIndex + 1) % list.length : -1;
        this.scrollActiveIntoView();
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.showCountryList = true;
        this.activeCountryIndex = list.length
          ? (this.activeCountryIndex - 1 + list.length) % list.length
          : -1;
        this.scrollActiveIntoView();
        break;
      case 'Enter':
        if (this.showCountryList && list[this.activeCountryIndex]) {
          e.preventDefault();
          this.selectCountry(list[this.activeCountryIndex]);
        }
        break;
      case 'Escape':
        if (this.showCountryList) {
          e.preventDefault();
          this.closeCountryList();
        }
        break;
    }
  }

  onCountryBlur(event: FocusEvent): void {
    const next = event.relatedTarget as HTMLElement | null;
    if (next && next.closest(`#${this.countryListId}`)) return;
    this.closeCountryList();
  }

  activeOptionId(): string | null {
    return this.showCountryList && this.activeCountryIndex >= 0
      ? `${this.countryListId}-${this.activeCountryIndex}`
      : null;
  }

  private scrollActiveIntoView(): void {
    if (!this.isBrowser) return;
    const id = this.activeOptionId();
    if (!id) return;
    setTimeout(() => document.getElementById(id)?.scrollIntoView({ block: 'nearest' }));
  }

  selectCountry(country: CountryInfo): void {
    this.selectedCountry = country;
    this.countrySearch = '';
    this.closeCountryList();
    this.countryError = '';
    if (country.code !== this.codeCountry || !this.serviceCode) {
      this.fetchServiceCode(country.code);
    }
  }

  clearCountry(): void {
    if (this.codeLocked) return;
    this.selectedCountry = null;
    this.countrySearch = '';
    this.serviceCode = '';
    this.codeCountry = '';
    this.codeState = 'idle';
    this.codeError = '';
    this.codeTaken = false;
    if (this.isBrowser) setTimeout(() => this.countryInput?.nativeElement.focus());
  }

  /** Allocates a code server-side, so it runs once per country pick, never per keystroke. */
  fetchServiceCode(countryCode: string = this.selectedCountry?.code ?? ''): void {
    if (!countryCode) return;
    this.codeState = 'loading';
    this.codeError = '';
    this.serviceCodeError = '';
    this.codeTaken = false;
    this.serviceCode = '';
    const request = ++this.codeRequest;
    // A late answer must not overwrite a newer pick, a cleared country or a
    // code that "Keep <code>" has locked again.
    const isCurrent = () =>
      request === this.codeRequest && countryCode === this.selectedCountry?.code && !this.codeLocked;
    this.applicationsService.generateServiceCode(countryCode).subscribe({
      next: (res) => {
        if (!isCurrent()) return;
        this.serviceCode = res?.serviceCode ?? '';
        this.codeCountry = countryCode;
        this.codeState = this.serviceCode ? 'idle' : 'error';
        this.codeError = this.serviceCode ? '' : 'No code is free in this country right now. Try again later or pick another country.';
        if (this.serviceCode) this.checkCode(this.serviceCode);
      },
      error: (err) => {
        if (!isCurrent()) return;
        this.codeState = 'error';
        this.codeError = describeHttpError(err, 'A service code could not be generated.');
      },
    });
  }

  private checkCode(code: string): void {
    if (!this.organisationId) return;
    this.applicationsService
      .checkDuplicate(this.organisationId, { serviceCode: code, excludeId: this.isEdit ? this.id : undefined })
      .pipe(catchError(() => of(null)))
      .subscribe((result) => {
        if (code === this.serviceCode) this.codeTaken = !!result?.serviceCodeExists;
      });
  }

  // Edit: change the country and code only after a confirmation.

  requestCodeChange(): void {
    this.showChangeCodeConfirm = true;
  }

  cancelCodeChange(): void {
    this.showChangeCodeConfirm = false;
  }

  confirmCodeChange(): void {
    this.showChangeCodeConfirm = false;
    this.codeUnlocked = true;
    // Picking a country, even the same one, now allocates a new code.
    this.selectedCountry = null;
    this.serviceCode = '';
    this.codeCountry = '';
    this.codeTaken = false;
    if (this.isBrowser) setTimeout(() => this.countryInput?.nativeElement.focus());
  }

  keepOriginalCode(): void {
    if (!this.original) return;
    this.codeUnlocked = false;
    this.serviceCode = this.original.serviceCode ?? '';
    this.selectedCountry =
      countryByCode(this.original.countryCode) ??
      (this.original.countryCode
        ? { code: this.original.countryCode, name: this.original.countryName || this.original.countryCode, flag: '', dialCode: '' }
        : null);
    this.codeCountry = this.selectedCountry?.code ?? '';
    this.codeState = 'idle';
    this.codeError = '';
    this.codeTaken = false;
    this.serviceCodeError = '';
    this.countryError = '';
  }

  // ── Callback ──

  setAuthType(value: string): void {
    this.authType = (this.AUTH_TYPES.find((a) => a.value === value)?.value ?? 'NONE') as UssdAuthType;
    this.credentialErrors = {};
    this.revealSecret = false;
  }

  clearCredentialError(key: string): void {
    if (this.credentialErrors[key]) {
      const { [key]: _, ...rest } = this.credentialErrors;
      this.credentialErrors = rest;
    }
  }

  // ── Steps ──

  private validateStep(step: FormStep): boolean {
    switch (step) {
      case 'basics': {
        const name = this.name.trim();
        this.nameError = !name ? 'Enter a name.' : this.nameTaken ? 'Another application already uses this name.' : '';
        this.countryError = this.selectedCountry ? '' : 'Pick the country subscribers dial from.';
        if (this.codeState === 'loading') {
          this.serviceCodeError = 'Wait for the service code.';
        } else if (!this.serviceCode.trim()) {
          this.serviceCodeError = this.selectedCountry ? 'Get a service code before you continue.' : '';
        } else if (this.codeTaken) {
          this.serviceCodeError = 'Another application already uses this code.';
        } else {
          this.serviceCodeError = '';
        }
        return !this.nameError && !this.countryError && !this.serviceCodeError && this.codeState !== 'loading';
      }
      case 'callback': {
        const url = this.callbackUrl.trim();
        if (!url) {
          this.callbackUrlError = 'Enter the URL that answers USSD requests.';
        } else if (!isHttpUrl(url)) {
          this.callbackUrlError = 'Enter a full URL starting with https://';
        } else {
          this.callbackUrlError = '';
        }
        this.credentialErrors = this.validateCredentials();
        return !this.callbackUrlError && !Object.keys(this.credentialErrors).length;
      }
      default:
        return true;
    }
  }

  private validateCredentials(): Record<string, string> {
    const errors: Record<string, string> = {};
    const need = (key: string, value: string, message: string) => {
      if (!value.trim()) errors[key] = message;
    };
    switch (this.authType) {
      case 'BEARER':
        need('bearerToken', this.bearerToken, 'Enter the token.');
        break;
      case 'BASIC':
        need('basicUsername', this.basicUsername, 'Enter the username.');
        need('basicPassword', this.basicPassword, 'Enter the password.');
        break;
      case 'API_KEY':
        need('apiKeyHeader', this.apiKeyHeader, 'Enter the header name, for example X-API-Key.');
        need('apiKey', this.apiKey, 'Enter the key.');
        break;
      case 'CUSTOM':
        need('customAuthHeader', this.customAuthHeader, 'Enter the header name.');
        need('customAuthValue', this.customAuthValue, 'Enter the header value.');
        break;
    }
    return errors;
  }

  /**
   * Show another step. The old step's buttons are destroyed with it, so focus
   * moves to the new step's heading, or to its first invalid field when the
   * step is shown because it failed validation.
   */
  private navigateToStep(step: FormStep, focus: 'heading' | 'invalid' = 'heading'): void {
    const navigation = this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { title: step },
      queryParamsHandling: 'merge',
    });
    if (!this.isBrowser) return;
    if (focus === 'heading') {
      const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
    }
    navigation.then(() =>
      setTimeout(() => (focus === 'heading' ? this.focusStepHeading() : this.focusFirstInvalid()))
    );
  }

  private focusStepHeading(): void {
    this.host.nativeElement.querySelector<HTMLElement>('.uf__step-focus')?.focus({ preventScroll: true });
  }

  /** The first field with an error, so its message (aria-describedby) is read out. */
  private focusFirstInvalid(): void {
    this.host.nativeElement
      .querySelector<HTMLElement>(
        '[aria-invalid="true"], .is-invalid :is(input, select, textarea), .uf__codefield.is-invalid ~ .uf__reg-actions .bk-btn'
      )
      ?.focus();
  }

  nextStep(): void {
    if (!this.validateStep(this.activeStep)) {
      if (this.isBrowser) setTimeout(() => this.focusFirstInvalid());
      return;
    }
    const idx = STEP_ORDER.indexOf(this.activeStep);
    if (idx < STEP_ORDER.length - 1) this.navigateToStep(STEP_ORDER[idx + 1]);
  }

  prevStep(): void {
    const idx = STEP_ORDER.indexOf(this.activeStep);
    if (idx > 0) this.navigateToStep(STEP_ORDER[idx - 1]);
  }

  goToStep(step: FormStep): void {
    if (STEP_ORDER.indexOf(step) < STEP_ORDER.indexOf(this.activeStep)) this.navigateToStep(step);
  }

  cancel(): void {
    if (this.isEdit && this.id) {
      this.router.navigate(['/organization/ussd-simulator/applications', this.id]);
    } else {
      this.router.navigate(['/organization/ussd-simulator/applications']);
    }
  }

  backToList(): void {
    this.router.navigate(['/organization/ussd-simulator/applications']);
  }

  // ── Submit ──

  private credentialFields(): CredentialFields {
    switch (this.authType) {
      case 'BEARER':
        return { callbackBearerToken: this.bearerToken.trim() };
      case 'BASIC':
        return { callbackBasicUsername: this.basicUsername.trim(), callbackBasicPassword: this.basicPassword };
      case 'API_KEY':
        return { callbackApiKeyHeader: this.apiKeyHeader.trim(), callbackApiKey: this.apiKey.trim() };
      case 'CUSTOM':
        return {
          callbackCustomAuthHeader: this.customAuthHeader.trim(),
          callbackCustomAuthValue: this.customAuthValue,
        };
      default:
        return {};
    }
  }

  submit(): void {
    if (this.isSubmitting) return;
    const organisationId = this.organisationId;
    if (!organisationId) return;

    for (const step of ['basics', 'callback'] as FormStep[]) {
      if (!this.validateStep(step)) {
        this.navigateToStep(step, 'invalid');
        return;
      }
    }
    const country = this.selectedCountry!;

    this.isSubmitting = true;
    this.submitError = '';

    if (this.isEdit) {
      const request = {
        id: this.id,
        name: this.name.trim(),
        alias: this.alias.trim(),
        serviceCode: this.serviceCode.trim(),
        description: this.description.trim() || undefined,
        callbackUrl1: this.callbackUrl.trim(),
        callbackMethod: CALLBACK_METHOD,
        callbackContentType: CALLBACK_CONTENT_TYPE,
        callbackAuthType: this.authType,
        callbackServiceName: this.serviceName.trim() || undefined,
        countryCode: country.code,
        countryName: country.name,
        ...this.credentialFields(),
      };
      this.applicationsService.updateApplication(organisationId, this.id, request).subscribe({
        next: () => {
          this.isSubmitting = false;
          this.toast.success(`${request.name} saved.`);
          this.router.navigate(['/organization/ussd-simulator/applications', this.id]);
        },
        error: (err) => {
          this.isSubmitting = false;
          this.submitError = describeHttpError(err, 'Your changes were not saved.');
        },
      });
      return;
    }

    const request = {
      name: this.name.trim(),
      alias: this.alias.trim(),
      serviceCode: this.serviceCode.trim() || undefined,
      description: this.description.trim() || undefined,
      callbackUrl1: this.callbackUrl.trim(),
      callbackMethod: CALLBACK_METHOD,
      callbackContentType: CALLBACK_CONTENT_TYPE,
      callbackAuthType: this.authType,
      callbackServiceName: this.serviceName.trim() || undefined,
      countryCode: country.code,
      countryName: country.name,
      passthrough: false,
      mnoServiceDataPassThrough: false,
      environment: 'DEVELOPMENT' as const,
      ussdOrganisationProfile: {
        organisationId,
        organisationName: this.userService.getInstitutionName ?? '',
        countryCode: country.code,
        countryName: country.name,
        status: 'PENDING' as const,
        serviceAccountId: this.serviceAccountId || undefined,
      },
      ...this.credentialFields(),
    };

    this.applicationsService.createApplication(organisationId, request).subscribe({
      next: (created) => {
        this.isSubmitting = false;
        this.toast.success(`${request.name} created in Development with code ${created?.serviceCode || request.serviceCode || ''}.`.replace(/ with code \.$/, '.'));
        if (created?.id) {
          this.router.navigate(['/organization/ussd-simulator/applications', created.id]);
        } else {
          this.router.navigate(['/organization/ussd-simulator/applications']);
        }
      },
      error: (err) => {
        this.isSubmitting = false;
        this.submitError = describeHttpError(err, 'The application was not created.');
      },
    });
  }
}

function isHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return (url.protocol === 'https:' || url.protocol === 'http:') && !!url.hostname;
  } catch {
    return false;
  }
}
